import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDenaliScopes, useSelectedDenaliScope } from '../DenaliAnalysis/filters/DenaliScopeProvider'
import { useExecuteQueryBlueprintQuery } from '../../../api/denali'
import ScopeSelect from '../DenaliAnalysis/DenaliScopePicker/ScopeSelect'
import { ScopeDisplayItem } from '../DenaliAnalysis/DenaliScopePicker/ScopeDisplayItem'

const ALL_ITEMS_VALUE = '__all_items__'

/**
 * Value-source picker: fetches options for the active scope by executing
 * a "source purpose" via execute-query-blueprint, then writes selected
 * raw values into DenaliScopeContext. Used for scopes whose backing
 * predicates are expression-type (e.g. WHERE x IN (:fromParameter)).
 *
 * Renders inside a DenaliScopeProvider that has an active scope with
 * scope.source.type === 'valueSource'.
 */
function BlueprintValueSourcePicker ({ blueprintCode, size, hideAvatars, multiSelect }) {
  const { selectScopeValues } = useDenaliScopes()
  const { scope, values } = useSelectedDenaliScope()

  const sourcePurposeCode = scope?.source?.purposeCode
  const valueColumn = scope?.source?.valueColumn
  const labelColumn = scope?.source?.labelColumn ?? valueColumn
  const allItemsLabel = scope?.labels?.allItems ?? 'All Items'

  const request = useMemo(() => {
    if (!blueprintCode || !sourcePurposeCode) return null
    return {
      code: blueprintCode,
      purposeCode: sourcePurposeCode,
      page: 0,
      pageSize: 1000
    }
  }, [blueprintCode, sourcePurposeCode])

  const { data, isLoading, isFetching } = useExecuteQueryBlueprintQuery(request, {
    enabled: !!request
  })

  const { options, pinnedOptions } = useMemo(() => {
    const pinned = [{
      label: allItemsLabel,
      value: ALL_ITEMS_VALUE,
      attributes: { isAllItems: true }
    }]

    const rows = data?.data ?? []
    const seen = new Set()
    const opts = []
    for (const row of rows) {
      const rawValue = row?.[valueColumn]
      if (rawValue === null || rawValue === undefined) continue
      const value = String(rawValue)
      if (seen.has(value)) continue
      seen.add(value)
      const label = row?.[labelColumn] != null ? String(row[labelColumn]) : value
      opts.push({
        label,
        value,
        attributes: { rawValue }
      })
    }
    return { options: opts, pinnedOptions: pinned }
  }, [data, valueColumn, labelColumn, allItemsLabel])

  const handleSelect = useCallback((selection) => {
    if (Array.isArray(selection)) {
      const next = selection
        .filter(s => !s?.attributes?.isAllItems)
        .map(s => s.attributes?.rawValue ?? s.value)
      selectScopeValues(next)
      return
    }
    if (selection?.attributes?.isAllItems) {
      selectScopeValues([])
      return
    }
    const raw = selection?.attributes?.rawValue ?? selection?.value
    selectScopeValues(raw === undefined || raw === null ? [] : [raw])
  }, [selectScopeValues])

  const selectedDisplay = useMemo(() => {
    if (!values?.length) {
      return { label: allItemsLabel, abbreviation: 'All' }
    }
    if (values.length > 1) {
      const multiLabel = scope?.labels?.multipleItems ?? 'Multiple'
      return { label: multiLabel, abbreviation: 'M' }
    }
    const v = String(values[0])
    const match = options.find(o => o.value === v)
    return { label: match?.label ?? v, abbreviation: (match?.label ?? v).slice(0, 2).toUpperCase() }
  }, [values, options, allItemsLabel, scope?.labels?.multipleItems])

  const currentlySelectedValues = useMemo(() => (values ?? []).map(v => String(v)), [values])

  return (
    <ScopeSelect
      options={options}
      pinnedOptions={pinnedOptions}
      isLoading={isLoading || isFetching}
      onSelect={handleSelect}
      currentlySelectedValues={currentlySelectedValues}
      multiSelect={multiSelect}
    >
      <ScopeDisplayItem
        name={selectedDisplay.label}
        subTitle={scope?.labels?.subTitle}
        abbreviation={selectedDisplay.abbreviation}
        hideAvatar={hideAvatars}
        size={size}
      />
    </ScopeSelect>
  )
}

BlueprintValueSourcePicker.propTypes = {
  /** Blueprint code under which the source purpose is defined. */
  blueprintCode: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'lg']),
  hideAvatars: PropTypes.bool,
  multiSelect: PropTypes.bool
}

BlueprintValueSourcePicker.defaultProps = {
  size: 'sm',
  multiSelect: false,
  hideAvatars: true
}

export default BlueprintValueSourcePicker
